/**
 * @pj/content — the game's authored content: maps, doors, warps, gates, NPCs,
 * and Levels. Everything here is data.
 *
 * Terminology: a **Level** is a stage of the game — its own map(s) with an exit
 * GATE that opens when the level's objective is met. Level 1 is the locked-house
 * surf quest (gate opens on finding every treasure); Level 2 is "Bhaav".
 *
 * The map model is deliberately Tiled-shaped (tile layers + a behavior per tile)
 * so that authored `.tmj` exports can replace the hand-built maps with no
 * engine changes. Tiles are semantic ids; the engine maps them to generated
 * GBA-style textures.
 */

import type { Effect } from "@pj/script";
import type { Dir } from "@pj/protocol";

/** A metatile behavior — what stepping on / interacting with the tile means.
 *  This is the seam every field mechanic (grass encounters, ledges, surf,
 *  cracked floors, warps…) extends. */
export type TileBehavior =
  | "normal"
  | "blocked"
  | "grass"
  | "water"
  | "ledge_down"
  /** A scripted door/gate slot — blocked while closed; a warp once opened. */
  | "door";

export interface TileDef {
  /** Semantic name the engine uses to pick a generated texture. */
  name: string;
  behavior: TileBehavior;
}

/** Palette: ground-layer tile indices → definition. */
export const PALETTE: readonly TileDef[] = [
  { name: "path", behavior: "normal" }, //          0 dirt path
  { name: "tall_grass", behavior: "grass" }, //     1 tall grass (encounters later)
  { name: "wall", behavior: "blocked" }, //         2 house wall (south face)
  { name: "water", behavior: "water" }, //          3 pond/ocean water
  { name: "tree", behavior: "blocked" }, //         4 tree
  { name: "floor_wood", behavior: "normal" }, //    5 interior wood floor
  { name: "lawn", behavior: "normal" }, //          6 short grass / town green
  { name: "roof_ridge", behavior: "blocked" }, //   7 roof top row
  { name: "roof_mid", behavior: "blocked" }, //     8 roof slope
  { name: "roof_eave", behavior: "blocked" }, //    9 roof bottom edge / eave
  { name: "window", behavior: "blocked" }, //      10 wall with window
  { name: "door_slot", behavior: "door" }, //      11 door position (entity overlays)
  { name: "sand", behavior: "normal" }, //         12 pond shore sand
  { name: "wall_interior", behavior: "blocked" }, // 13 interior walls
  { name: "mat", behavior: "normal" }, //          14 interior exit mat (warp)
  { name: "table", behavior: "blocked" }, //       15 interior table
  { name: "flowers", behavior: "normal" }, //      16 lawn with flowers (animated)
  { name: "gate_slot", behavior: "door" }, //      17 level-exit gate (entity overlays)
];

/** Step-on warp: arriving on (tx,ty) teleports to the destination. */
export interface WarpDef {
  tx: number;
  ty: number;
  destMapId: string;
  destTx: number;
  destTy: number;
  facing: Dir;
}

export interface MapData {
  id: string;
  /** Tile dimensions. */
  width: number;
  height: number;
  /** Ground layer: palette indices, row-major, length width*height. */
  ground: number[];
  /** Where a freshly-joined player spawns (tile coords). */
  spawn: { tx: number; ty: number };
  /** Step-on warps (interior exit mats, cave mouths, …). */
  warps: WarpDef[];
}

export function tileIndexAt(map: MapData, tx: number, ty: number): number | null {
  if (tx < 0 || ty < 0 || tx >= map.width || ty >= map.height) return null;
  return map.ground[ty * map.width + tx] ?? null;
}

export function tileDefAt(map: MapData, tx: number, ty: number): TileDef | null {
  const idx = tileIndexAt(map, tx, ty);
  if (idx === null) return null;
  return PALETTE[idx] ?? null;
}

export function warpAt(map: MapData, tx: number, ty: number): WarpDef | null {
  return map.warps.find((w) => w.tx === tx && w.ty === ty) ?? null;
}

const maps = new Map<string, MapData>();

export function registerMap(map: MapData): void {
  maps.set(map.id, map);
}

export function getMap(id: string): MapData {
  const map = maps.get(id);
  if (!map) throw new Error(`@pj/content: unknown map '${id}'`);
  return map;
}

export const DEMO_MAP_ID = "demo_town";
export const HOUSE_MAP_ID = "house_inside";
export const BHAAV_MAP_ID = "bhaav_town";
export const BHAAV_HOUSE_MAP_ID = "bhaav_house";

const GATE_TILE = 17;

/** Build the hand-authored town: lawn ringed by trees, a house with a locked
 *  door in the NW, a sandy pond in the SE, paths, tall-grass patches. */
function buildDemoTown(): MapData {
  const width = 24;
  const height = 18;
  const ground = new Array<number>(width * height).fill(6); // base: lawn
  const set = (x: number, y: number, v: number) => {
    if (x >= 0 && y >= 0 && x < width && y < height) ground[y * width + x] = v;
  };
  const fill = (x0: number, y0: number, x1: number, y1: number, v: number) => {
    for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) set(x, y, v);
  };

  // Tree border (blocked) framing the playable area.
  for (let x = 0; x < width; x++) {
    set(x, 0, 4);
    set(x, height - 1, 4);
  }
  for (let y = 0; y < height; y++) {
    set(0, y, 4);
    set(width - 1, y, 4);
  }
  // A couple of interior trees for depth.
  set(9, 3, 4);
  set(16, 6, 4);
  set(2, 7, 4);

  // Cross of dirt paths through the town green.
  const cy = 9;
  const cx = 12;
  for (let x = 1; x < width - 1; x++) set(x, cy, 0);
  for (let y = 1; y < height - 1; y++) set(cx, y, 0);

  // The east gate to LEVEL 2 sits in the tree border at the end of the path.
  set(width - 1, cy, GATE_TILE);

  // The old house (NW): 5 wide — 3 roof rows, then the south wall face with
  // window / door / window. The door tile is a scripted "door" slot.
  fill(3, 2, 7, 2, 7); //  roof ridge
  fill(3, 3, 7, 3, 8); //  roof slope
  fill(3, 4, 7, 4, 9); //  roof eave
  set(3, 5, 2); //         wall
  set(4, 5, 10); //        window
  set(5, 5, 11); //        DOOR
  set(6, 5, 10); //        window
  set(7, 5, 2); //         wall
  // Path from the door down to the main path.
  set(5, 6, 0);
  set(5, 7, 0);
  set(5, 8, 0);

  // The pond (SE): sand shore ring, water with rounded corners.
  fill(14, 10, 21, 16, 12); // sand apron
  fill(15, 11, 20, 15, 3); //  water
  set(15, 11, 12); //          cut corners → rounded pond
  set(20, 11, 12);
  set(15, 15, 12);
  set(20, 15, 12);

  // Tall-grass patches (future wild-encounter zones).
  fill(2, 11, 5, 14, 1); //   SW
  fill(17, 2, 21, 4, 1); //   NE

  // Flower beds on the lawn.
  for (const [fx, fy] of [
    [9, 6],
    [10, 7],
    [14, 6],
    [15, 7],
    [8, 12],
    [10, 13],
    [2, 4],
    [13, 3],
  ] as const) {
    set(fx, fy, 16);
  }

  return { id: DEMO_MAP_ID, width, height, ground, spawn: { tx: cx, ty: cy }, warps: [] };
}

/** The inside of the old house: one small Hoenn-style room. The exit mat at
 *  the bottom warps back outside, one tile below the door, facing south. */
function buildHouseInside(): MapData {
  const width = 10;
  const height = 8;
  const ground = new Array<number>(width * height).fill(5); // wood floor
  const set = (x: number, y: number, v: number) => {
    if (x >= 0 && y >= 0 && x < width && y < height) ground[y * width + x] = v;
  };
  for (let x = 0; x < width; x++) {
    set(x, 0, 13);
    set(x, 1, 13); // tall back wall, GBA-style two rows
    set(x, height - 1, 13);
  }
  for (let y = 0; y < height; y++) {
    set(0, y, 13);
    set(width - 1, y, 13);
  }
  // A table against the back wall.
  set(2, 3, 15);
  set(3, 3, 15);
  // Exit mat on the bottom floor row.
  set(4, 6, 14);

  return {
    id: HOUSE_MAP_ID,
    width,
    height,
    ground,
    spawn: { tx: 4, ty: 6 },
    warps: [{ tx: 4, ty: 6, destMapId: DEMO_MAP_ID, destTx: 5, destTy: 6, facing: "down" }],
  };
}

/** LEVEL 2 town: AANYA's town. A west gate back to LEVEL 1, an open house in
 *  the north with the computer (and the closet…), AANYA on the green, and the
 *  locked east gate to LEVEL 3. */
function buildBhaavTown(): MapData {
  const width = 24;
  const height = 18;
  const ground = new Array<number>(width * height).fill(6); // base: lawn
  const set = (x: number, y: number, v: number) => {
    if (x >= 0 && y >= 0 && x < width && y < height) ground[y * width + x] = v;
  };
  const fill = (x0: number, y0: number, x1: number, y1: number, v: number) => {
    for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) set(x, y, v);
  };

  // Tree border.
  for (let x = 0; x < width; x++) {
    set(x, 0, 4);
    set(x, height - 1, 4);
  }
  for (let y = 0; y < height; y++) {
    set(0, y, 4);
    set(width - 1, y, 4);
  }
  // Interior trees for depth.
  set(4, 3, 4);
  set(18, 5, 4);
  set(3, 13, 4);
  set(19, 13, 4);

  // Main path west gate → east gate.
  const cy = 9;
  for (let x = 1; x < width - 1; x++) set(x, cy, 0);
  // Both gates sit in the tree border on the path row.
  set(0, cy, GATE_TILE);
  set(width - 1, cy, GATE_TILE);

  // AANYA's house (N, center): roof rows then wall face with the open door.
  fill(8, 2, 12, 2, 7); //  roof ridge
  fill(8, 3, 12, 3, 8); //  roof slope
  fill(8, 4, 12, 4, 9); //  roof eave
  set(8, 5, 2); //          wall
  set(9, 5, 10); //         window
  set(10, 5, 11); //        DOOR (open — no key needed)
  set(11, 5, 10); //        window
  set(12, 5, 2); //         wall
  // Path from the door down to the main path.
  set(10, 6, 0);
  set(10, 7, 0);
  set(10, 8, 0);

  // Tall grass in the SE corner; flower beds dotted around.
  fill(17, 12, 21, 15, 1);
  for (const [fx, fy] of [
    [6, 6],
    [7, 7],
    [14, 7],
    [15, 11],
    [13, 11],
    [5, 12],
    [16, 3],
    [2, 5],
  ] as const) {
    set(fx, fy, 16);
  }

  return { id: BHAAV_MAP_ID, width, height, ground, spawn: { tx: 2, ty: cy }, warps: [] };
}

/** Inside AANYA's house: the closet tucked in the left corner, the computer
 *  on the right. The exit mat warps back outside, below the door. */
function buildBhaavHouse(): MapData {
  const width = 10;
  const height = 8;
  const ground = new Array<number>(width * height).fill(5); // wood floor
  const set = (x: number, y: number, v: number) => {
    if (x >= 0 && y >= 0 && x < width && y < height) ground[y * width + x] = v;
  };
  for (let x = 0; x < width; x++) {
    set(x, 0, 13);
    set(x, 1, 13);
    set(x, height - 1, 13);
  }
  for (let y = 0; y < height; y++) {
    set(0, y, 13);
    set(width - 1, y, 13);
  }
  // A table in the middle of the back wall.
  set(4, 3, 15);
  set(5, 3, 15);
  // Exit mat on the bottom floor row.
  set(4, 6, 14);

  return {
    id: BHAAV_HOUSE_MAP_ID,
    width,
    height,
    ground,
    spawn: { tx: 4, ty: 6 },
    warps: [{ tx: 4, ty: 6, destMapId: BHAAV_MAP_ID, destTx: 10, destTy: 6, facing: "down" }],
  };
}

registerMap(buildDemoTown());
registerMap(buildHouseInside());
registerMap(buildBhaavTown());
registerMap(buildBhaavHouse());

// --- abilities --------------------------------------------------------------

export interface AbilityDef {
  id: string;
  name: string;
  description: string;
}

export const ABILITIES: Record<string, AbilityDef> = {
  surf: {
    id: "surf",
    name: "Surf",
    description: "Cross deep water. Face the water's edge and press SPACE.",
  },
};

/** Gen-3 surf prompt + use lines (Emerald wording). */
export const SURF_PROMPT_LINES = ["The water is dyed a deep blue…", "Would you like to SURF?"];
export const SURF_USED_LINES = ["PURANJAY used SURF!"];

// --- level building blocks ----------------------------------------------------

export interface TreasureDef {
  id: string;
  mapId: string;
  tx: number;
  ty: number;
  /** Render hint: "key" | "berry" | "charm" | "pearl" | … */
  sprite: string;
  /** Effects applied when the item is picked up. */
  effects: Effect[];
}

export interface SignDef {
  id: string;
  mapId: string;
  tx: number;
  ty: number;
  effects: Effect[];
}

/** A locked, scripted door. Blocked until unlocked with the required item;
 *  afterwards walking into it warps to the destination (a house interior).
 *  Doors with no `requiresItem` start open. */
export interface DoorDef {
  id: string;
  mapId: string;
  tx: number;
  ty: number;
  requiresItem?: string;
  /** Message boxes when bumped/pressed while locked, without the item. */
  lockedEffects: Effect[];
  /** Message boxes when unlocking with the item (door opens after). */
  unlockEffects: Effect[];
  warp: { mapId: string; tx: number; ty: number; facing: Dir };
}

/** A level-exit gate in the map border. Closed until the sim opens it (level
 *  objective met); once open, walking through warps into the destination level. */
export interface GateDef {
  id: string;
  mapId: string;
  tx: number;
  ty: number;
  /** Where the gate leads. `null` = the next level doesn't exist yet. */
  dest: { levelId: string; mapId: string; tx: number; ty: number; facing: Dir } | null;
  /** Shown when bumped/pressed while closed. */
  closedEffects: Effect[];
}

/** A scripted fixture with bespoke sim behavior (the computer, the closet…).
 *  Solid like a sign; the sim dispatches interaction on `kind`. */
export interface MachineDef {
  id: string;
  kind: "computer" | "closet";
  mapId: string;
  tx: number;
  ty: number;
  sprite: string;
}

export interface NpcDef {
  id: string;
  mapId: string;
  tx: number;
  ty: number;
  /** Base actor sprite ("aanya"); the sim may render variants ("aanya_cry"). */
  sprite: string;
}

/** A game Level: a stage with its own maps, objective, and exit gate. */
export interface LevelDef {
  id: string;
  num: number;
  name: string;
  /** Entry map (player spawn when starting/loading into this level). */
  mapId: string;
  /** Every map belonging to this level (for map → level lookup). */
  maps: string[];
  objectiveLabel: string;
  treasures: TreasureDef[];
  signs: SignDef[];
  doors: DoorDef[];
  gates: GateDef[];
  machines: MachineDef[];
  npcs: NpcDef[];
  /** Applied once when the level objective completes. */
  reward: Effect[];
}

const levels = new Map<string, LevelDef>();

export function registerLevel(level: LevelDef): void {
  levels.set(level.id, level);
}

export function getLevel(id: string): LevelDef {
  const level = levels.get(id);
  if (!level) throw new Error(`@pj/content: unknown level '${id}'`);
  return level;
}

/** All levels, ordered by number. */
export function listLevels(): LevelDef[] {
  return [...levels.values()].sort((a, b) => a.num - b.num);
}

export function levelForMap(mapId: string): LevelDef {
  for (const level of levels.values()) if (level.maps.includes(mapId)) return level;
  throw new Error(`@pj/content: no level owns map '${mapId}'`);
}

// --- LEVEL 1: The Locked House ------------------------------------------------
// The RUSTY KEY → locked house → SURF CHARM → pearl chain. Mirrors the Gen-3
// gating recipe (Basement Key → New Mauville): the key is a bag item, the door
// checks it, the HM waits behind the door, the pearl waits across the water.

export const LEVEL1_ID = "level1";
export const LEVEL2_ID = "level2";

const HOUSE_DOOR: DoorDef = {
  id: "house_door",
  mapId: DEMO_MAP_ID,
  tx: 5,
  ty: 5,
  requiresItem: "rusty_key",
  lockedEffects: [
    { kind: "dialogue", lines: ["The door is locked."] },
    { kind: "dialogue", lines: ["I think a key is needed", "for this."] },
  ],
  unlockEffects: [
    { kind: "dialogue", lines: ["PURANJAY used the RUSTY KEY."] },
    { kind: "dialogue", lines: ["The door opened!"] },
  ],
  warp: { mapId: HOUSE_MAP_ID, tx: 4, ty: 6, facing: "up" },
};

registerLevel({
  id: LEVEL1_ID,
  num: 1,
  name: "The Locked House",
  mapId: DEMO_MAP_ID,
  maps: [DEMO_MAP_ID, HOUSE_MAP_ID],
  objectiveLabel: "Find every treasure",
  signs: [
    {
      id: "sign_welcome",
      mapId: DEMO_MAP_ID,
      tx: 11,
      ty: 10,
      effects: [
        { kind: "dialogue", lines: ["PURANJAY TOWN", "A peaceful town of beginnings."] },
        { kind: "dialogue", lines: ["NOTICE: The old house is locked.", "Its key was lost in the tall grass…"] },
      ],
    },
    {
      id: "sign_pond",
      mapId: DEMO_MAP_ID,
      tx: 14,
      ty: 10,
      effects: [
        { kind: "dialogue", lines: ["The pond is dyed a deep blue…", "Something glitters out there."] },
      ],
    },
  ],
  doors: [HOUSE_DOOR],
  gates: [
    {
      id: "gate_level1",
      mapId: DEMO_MAP_ID,
      tx: 23,
      ty: 9,
      dest: { levelId: LEVEL2_ID, mapId: BHAAV_MAP_ID, tx: 1, ty: 9, facing: "right" },
      closedEffects: [
        { kind: "dialogue", lines: ["The GATE to LEVEL 2 is shut tight."] },
        { kind: "dialogue", lines: ["Maybe it opens when every", "treasure is found…"] },
      ],
    },
  ],
  machines: [],
  npcs: [],
  treasures: [
    {
      id: "key",
      mapId: DEMO_MAP_ID,
      tx: 19,
      ty: 3, // NE tall-grass patch
      sprite: "key",
      effects: [
        { kind: "giveItem", item: "rusty_key" },
        { kind: "dialogue", lines: ["PURANJAY found the RUSTY KEY!"] },
        { kind: "dialogue", lines: ["PURANJAY put away the RUSTY KEY", "in the KEY ITEMS POCKET."] },
        { kind: "addExp", amount: 30 },
      ],
    },
    {
      id: "surf_charm",
      mapId: HOUSE_MAP_ID,
      tx: 7,
      ty: 4,
      sprite: "charm",
      effects: [
        { kind: "grantAbility", ability: "surf" },
        { kind: "dialogue", lines: ["PURANJAY found the SURF CHARM!"] },
        { kind: "dialogue", lines: ["With the SURF CHARM, you can", "travel across deep water!"] },
        { kind: "dialogue", lines: ["Face the water's edge and", "press SPACE to surf."] },
        { kind: "addExp", amount: 60 },
      ],
    },
    {
      id: "berry",
      mapId: DEMO_MAP_ID,
      tx: 9,
      ty: 13,
      sprite: "berry",
      effects: [
        { kind: "dialogue", lines: ["PURANJAY found a sweet BERRY!"] },
        { kind: "addExp", amount: 20 },
      ],
    },
    {
      id: "pearl",
      mapId: DEMO_MAP_ID,
      tx: 17,
      ty: 13,
      sprite: "pearl",
      effects: [
        { kind: "dialogue", lines: ["PURANJAY found a shimmering", "PEARL!"] },
        { kind: "addExp", amount: 40 },
      ],
    },
  ],
  reward: [
    { kind: "dialogue", lines: ["You found every treasure!"] },
    { kind: "dialogue", lines: ["Far to the east, something", "creaked open…"] },
    { kind: "dialogue", lines: ["The GATE to LEVEL 2 is open!"] },
    { kind: "addExp", amount: 200 },
  ],
});

// --- LEVEL 2: Bhaav -------------------------------------------------------------
// AANYA is crying for bhaav (attention). Solve the computer's sudoku, then hug
// her — before the timer runs out. (There is also a hidden, never-hinted way:
// her favourite TOP is folded in the closet…)

registerLevel({
  id: LEVEL2_ID,
  num: 2,
  name: "Bhaav",
  mapId: BHAAV_MAP_ID,
  maps: [BHAAV_MAP_ID, BHAAV_HOUSE_MAP_ID],
  objectiveLabel: "Give AANYA bhaav",
  signs: [
    {
      id: "sign_bhaav",
      mapId: BHAAV_MAP_ID,
      tx: 11,
      ty: 10,
      effects: [
        { kind: "dialogue", lines: ["BHAAV TOWN", "Where attention is the currency."] },
      ],
    },
  ],
  doors: [
    {
      id: "bhaav_house_door",
      mapId: BHAAV_MAP_ID,
      tx: 10,
      ty: 5,
      lockedEffects: [],
      unlockEffects: [],
      warp: { mapId: BHAAV_HOUSE_MAP_ID, tx: 4, ty: 6, facing: "up" },
    },
  ],
  gates: [
    {
      id: "gate_level2_back",
      mapId: BHAAV_MAP_ID,
      tx: 0,
      ty: 9,
      dest: { levelId: LEVEL1_ID, mapId: DEMO_MAP_ID, tx: 22, ty: 9, facing: "left" },
      closedEffects: [],
    },
    {
      id: "gate_level2",
      mapId: BHAAV_MAP_ID,
      tx: 23,
      ty: 9,
      dest: null, // LEVEL 3 doesn't exist yet
      closedEffects: [
        { kind: "dialogue", lines: ["It's locked.", "I should give AANYA her bhaav first."] },
      ],
    },
  ],
  machines: [
    { id: "computer", kind: "computer", mapId: BHAAV_HOUSE_MAP_ID, tx: 7, ty: 2, sprite: "computer" },
    { id: "closet", kind: "closet", mapId: BHAAV_HOUSE_MAP_ID, tx: 1, ty: 2, sprite: "closet" },
  ],
  npcs: [{ id: "aanya", mapId: BHAAV_MAP_ID, tx: 14, ty: 10, sprite: "aanya" }],
  treasures: [],
  reward: [
    { kind: "dialogue", lines: ["AANYA got her bhaav!"] },
    { kind: "addExp", amount: 300 },
  ],
});

// --- dates -----------------------------------------------------------------------

/** YYYY-MM-DD for a date (local). Used for "come back tomorrow" level gating. */
export function todayKey(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
